<template>
  <Teleport to="body">
    <Transition name="fade">
      <div 
        v-if="isVisible" 
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50"
        @click.self="handleBackdropClick">
        <Transition name="scale">
          <div 
            v-if="isVisible"
            class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-slate-700"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId">
            
            <!-- Icon -->
            <div class="flex justify-center mb-4">
              <div 
                class="rounded-full p-3"
                :class="iconBgClass">
                <!-- Success Icon -->
                <svg 
                  v-if="type === 'success'" 
                  class="w-12 h-12" 
                  :class="iconColorClass"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                
                <!-- Error Icon -->
                <svg 
                  v-else-if="type === 'error'" 
                  class="w-12 h-12"
                  :class="iconColorClass"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                
                <!-- Warning Icon -->
                <svg 
                  v-else-if="type === 'warning'" 
                  class="w-12 h-12"
                  :class="iconColorClass"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                
                <!-- Info Icon -->
                <svg 
                  v-else-if="type === 'info'" 
                  class="w-12 h-12"
                  :class="iconColorClass"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>

            <!-- Title -->
            <h3 
              :id="titleId"
              class="text-xl font-semibold text-center mb-3 text-gray-900 dark:text-gray-100">
              {{ title }}
            </h3>

            <!-- Text -->
            <p 
              v-if="text"
              class="text-center text-gray-600 dark:text-gray-400 mb-6">
              {{ text }}
            </p>

            <!-- HTML Content -->
            <div 
              v-if="html"
              class="text-center text-gray-600 dark:text-gray-400 mb-6"
              v-html="html">
            </div>

            <!-- Buttons -->
            <div class="flex gap-3">
              <button
                v-if="showCancelButton"
                @click="handleCancel"
                class="flex-1 py-2.5 px-4 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium">
                {{ cancelButtonText }}
              </button>
              
              <button
                @click="handleConfirm"
                class="flex-1 py-2.5 px-4 rounded-lg text-white transition-colors font-medium"
                :class="confirmButtonClass">
                {{ confirmButtonText }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'AlertModal',
  data() {
    return {
      isVisible: false,
      type: 'info',
      title: '',
      text: '',
      html: '',
      showCancelButton: false,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancelar',
      resolvePromise: null,
      titleId: `alert-title-${Math.random().toString(36).substr(2, 9)}`
    };
  },
  computed: {
    iconBgClass() {
      const classes = {
        success: 'bg-green-100 dark:bg-green-900/30',
        error: 'bg-red-100 dark:bg-red-900/30',
        warning: 'bg-yellow-100 dark:bg-yellow-900/30',
        info: 'bg-blue-100 dark:bg-blue-900/30'
      };
      return classes[this.type] || classes.info;
    },
    iconColorClass() {
      const classes = {
        success: 'text-green-600 dark:text-green-400',
        error: 'text-red-600 dark:text-red-400',
        warning: 'text-yellow-600 dark:text-yellow-400',
        info: 'text-blue-600 dark:text-blue-400'
      };
      return classes[this.type] || classes.info;
    },
    confirmButtonClass() {
      const classes = {
        success: 'bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700',
        error: 'bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700',
        warning: 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-600 dark:hover:bg-yellow-700',
        info: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700'
      };
      return classes[this.type] || classes.info;
    }
  },
  methods: {
    show(options = {}) {
      this.type = options.icon || options.type || 'info';
      this.title = options.title || '';
      this.text = options.text || '';
      this.html = options.html || '';
      this.showCancelButton = options.showCancelButton || false;
      this.confirmButtonText = options.confirmButtonText || 'OK';
      this.cancelButtonText = options.cancelButtonText || 'Cancelar';
      
      this.isVisible = true;

      return new Promise((resolve) => {
        this.resolvePromise = resolve;
      });
    },
    handleConfirm() {
      this.isVisible = false;
      if (this.resolvePromise) {
        this.resolvePromise({ isConfirmed: true, value: true });
      }
    },
    handleCancel() {
      this.isVisible = false;
      if (this.resolvePromise) {
        this.resolvePromise({ isConfirmed: false, isDismissed: true });
      }
    },
    handleBackdropClick() {
      this.handleCancel();
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-enter-active,
.scale-leave-active {
  transition: all 0.2s ease;
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
